'use client'

import { LoadingButton } from '@7f-ui/components/button/loading-button'
import { TextField } from '@7f-ui/components/text-field/text-field'
import { useToast } from '@7f-ui/components/toast/toast-provider'
import { useMediaState } from '@7f-ui/hooks/use-media-state'
import { NewsletterSignupDocument } from '@data-access/lib/gql/footer/documents'
import { useGraphQLMutation } from '@data-access/lib/graphql-query'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { EnvelopeSimple } from '@phosphor-icons/react'

import Image from 'next/image'
import Link from 'next/link'
import { useForm } from 'react-hook-form'
import * as v from 'valibot'

type FormData = {
  email: string
  firstName: string
}

export function NewsletterSignUpModule() {
  const isMobile = useMediaState('mobile')

  const toast = useToast()
  const { mutate, isPending } = useGraphQLMutation(NewsletterSignupDocument)

  const validationSchema = v.object({
    email: v.pipe(v.string(), v.email('Bitte gib eine gültige E-Mail-Adresse ein')),
    firstName: v.string('Bitte gib deinen Vornamen ein'),
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: valibotResolver(validationSchema),
    defaultValues: { email: '', firstName: '' },
  })

  const onSuccess = () => {
    toast({
      type: 'success',
      message: 'Du hast dich erfolgreich registriert!',
    })
    reset()
  }

  const onSubmit = ({ email, firstName }: FormData) => {
    mutate({ input: { email, firstName } }, { onSuccess })
  }

  return (
    <div className="fluid-container mt-12 mb-6 sm:my-12 md:my-16">
      <div className="rounded-lg bg-sand p-6">
        <div className="flex flex-col items-center sm:flex-row">
          <div className="w-full sm:mr-4 sm:max-w-[266px] md:mr-9 md:max-w-[400px]">
            <div className="relative mb-4 aspect-4/3 w-full sm:mb-0 sm:aspect-3/4 md:aspect-square">
              <Image
                src="/images/newsletter/travel.jpg"
                alt="Wandern"
                sizes="(max-width: 768px) 80vw, (max-width: 1200px) 60vw"
                className="size-full rounded-2xl object-cover"
                fill
              />
            </div>
          </div>

          <div>
            <h2 className="mb-4 font-heading2 sm:mb-6">Bereit für deine Reise?</h2>

            <p className="mb-6 font-body-copy sm:mb-8 md:font-body-copy">
              Kleine Alltagsflucht oder ausgedehnter Urlaub: Wir schicken dir einen Rucksack voller
              Inspiration, Tipps und Reiselust. Natürlich kostenlos und du kannst dich jederzeit
              wieder abmelden – falls du das wirklich möchtest :)
            </p>

            <form
              className="w-full md:w-auto"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              id="signup-form"
            >
              <div className="flex flex-col gap-6 sm:flex-row">
                <TextField
                  className="w-full"
                  type="text"
                  label="Dein Vorname"
                  {...register('firstName')}
                  aria-invalid={errors.firstName ? 'true' : 'false'}
                  error={errors.firstName?.message}
                />

                <TextField
                  className="w-full"
                  type="email"
                  label="Deine E-Mail Adresse"
                  IconStart={EnvelopeSimple}
                  {...register('email')}
                  aria-invalid={errors.email ? 'true' : 'false'}
                  error={errors.email?.message}
                />
              </div>
              <div className="mt-6">
                <LoadingButton isLoading={isPending} full={isMobile} type="submit">
                  Jetzt anmelden
                </LoadingButton>
              </div>
            </form>

            <p className="mt-4 font-base-copy sm:mt-3">
              {'Wir verarbeiten deine Daten gemäß unserer '}
              <Link href="/datenschutz" className="underline underline-offset-2" target="_blank">
                Datenschutzerklärung
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
